import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Button,
  useDisclosure,
  Text,
  VStack,
  Flex,
  Box,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Center,
  Spinner,
  Tooltip,
  NumberInput,
  NumberInputField,
  Select,
  Input,
} from '@chakra-ui/react';
import {
  getActiveOrganizations,
  getSupportLoginDetails,
  getIntegrationDetails,
  getOrgTokenUsageBulk,
  OrgTokenUsageResponse,
} from '@devd-client/devd/auth';
import {
  useUpdatePullDays,
  useUpdateSubscriptionPlan,
  useUpdateEnabledModules,
  useDeactivateAllActiveIntegrations,
  useReactivateIntegrations,
} from './api';
import {
  NewDataGrid,
  Paginate,
  InfoIcon,
  PageHeader,
  useToastHook,
} from '@devd-client/devd/components';
import { EditPencilIcon } from '@devd-client/devd/components';
import { SearchInputBar, useDebounce } from '@devd-client/devd/components';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  IntegrationDetailsModal,
  IntegrationDetail,
} from './IntegrationDetailsModal';
import { EnabledModulesModal } from './EnabledModulesModal';
import { IntegrationToggleConfirmDialog } from './IntegrationToggleConfirmDialog';

interface EnabledModules {
  [key: string]: boolean | null;
}

interface ActiveIntegration {
  integrationType: string;
  integration: string;
}

interface Organization {
  organizationName: string;
  organizationId: string;
  subscriptionPlan: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  pullDays: number;
  activeIntegrations: ActiveIntegration[];
  enabledModules: EnabledModules;
}

interface PaginatedResponse {
  dto: Organization[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

interface LoginDetails {
  [key: string]: string;
}

export function ActiveOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationDetail | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loginDetails, setLoginDetails] = useState<LoginDetails | null>(null);
  const {
    isOpen: isIntegrationModalOpen,
    onOpen: onIntegrationModalOpen,
    onClose: onIntegrationModalClose,
  } = useDisclosure();
  const {
    isOpen: isWarningOpen,
    onOpen: onWarningOpen,
    onClose: onWarningClose,
  } = useDisclosure();
  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();
  const {
    isOpen: isPullDaysOpen,
    onOpen: onPullDaysOpen,
    onClose: onPullDaysClose,
  } = useDisclosure();
  const {
    isOpen: isPlanOpen,
    onOpen: onPlanOpen,
    onClose: onPlanClose,
  } = useDisclosure();
  const {
    isOpen: isModulesOpen,
    onOpen: onModulesOpen,
    onClose: onModulesClose,
  } = useDisclosure();
  const {
    isOpen: isIntegrationToggleOpen,
    onOpen: onIntegrationToggleOpen,
    onClose: onIntegrationToggleClose,
  } = useDisclosure();
  const cancelRef = useRef(null);
  const [newToast] = useToastHook();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLoadingIntegration, setIsLoadingIntegration] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState<{
    currentPage: number;
    totalPages: number;
  }>({ currentPage: 0, totalPages: 0 });
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  const [tokenUsageByOrg, setTokenUsageByOrg] = useState<
    Record<string, OrgTokenUsageResponse>
  >({});
  const [isLoadingTokenUsage, setIsLoadingTokenUsage] = useState(false);

  const [editPullDaysVal, setEditPullDaysVal] = useState<number>(0);
  const [editPlanVal, setEditPlanVal] = useState<string>('');
  const [editEndDateVal, setEditEndDateVal] = useState<string>('');
  const [editModules, setEditModules] = useState<EnabledModules>({});
  const [integrationToggleAction, setIntegrationToggleAction] = useState<
    'deactivate' | 'reactivate' | null
  >(null);

  const { mutate: updatePullDaysMutation, isLoading: isUpdatingPullDays } =
    useUpdatePullDays();
  const {
    mutate: updateSubscriptionPlanMutation,
    isLoading: isUpdatingSubscription,
  } = useUpdateSubscriptionPlan();
  const { mutate: updateEnabledModulesMutation, isLoading: isUpdatingModules } =
    useUpdateEnabledModules();
  const {
    mutate: deactivateAllIntegrationsMutation,
    isLoading: isDeactivatingIntegrations,
  } = useDeactivateAllActiveIntegrations();
  const {
    mutate: reactivateIntegrationsMutation,
    isLoading: isReactivatingIntegrations,
  } = useReactivateIntegrations();

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const data: PaginatedResponse = await getActiveOrganizations(
        currentPage,
        pageSize,
        'createdAt',
        debouncedSearch
      );
      const orgs = data?.dto || [];
      setOrganizations(orgs);
      setPaginationData({
        currentPage: data?.currentPage || 0,
        totalPages: data?.totalPages || 0,
      });

      const orgIds = orgs.map((o) => o.organizationId).filter(Boolean);
      if (orgIds.length) {
        setIsLoadingTokenUsage(true);
        try {
          const usage: OrgTokenUsageResponse[] = await getOrgTokenUsageBulk(
            orgIds
          );
          const byOrg: Record<string, OrgTokenUsageResponse> = {};
          usage.forEach((u) => {
            if (u?.orgId) {
              byOrg[u.orgId] = u;
            }
          });
          setTokenUsageByOrg(byOrg);
        } catch (err) {
          console.error('Error fetching token usage for orgs:', err);
          setTokenUsageByOrg({});
        } finally {
          setIsLoadingTokenUsage(false);
        }
      } else {
        setTokenUsageByOrg({});
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleIntegrationClick = async (
    integration: ActiveIntegration,
    orgId: string
  ) => {
    setSelectedOrgId(orgId);
    setSelectedIntegration(null); // Clear previous integration data
    setIsLoadingIntegration(true);
    onIntegrationModalOpen(); // Open modal immediately

    try {
      const detailedIntegration = await getIntegrationDetails(
        orgId,
        integration.integrationType,
        integration.integration
      );
      setSelectedIntegration(detailedIntegration);
    } catch (error) {
      newToast({
        message: 'Error retrieving integration details',
        status: 'error',
      });
      onIntegrationModalClose(); // Close modal on error
    } finally {
      setIsLoadingIntegration(false);
    }
  };

  const handleShowAccountDetails = (orgId: string) => {
    setSelectedOrgId(orgId);
    onWarningOpen();
  };

  const handleAccept = async () => {
    onWarningClose();
    setIsLoadingDetails(true);
    try {
      const data = await getSupportLoginDetails(selectedOrgId);
      setLoginDetails(data);
      onDetailsOpen();
    } catch (error) {
      newToast({
        message: 'Error retrieving login details',
        status: 'error',
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handlePageClick = useCallback((page: number) => {
    setCurrentPage(page - 1);
  }, []);

  const openPullDaysModal = (org: Organization) => {
    setSelectedOrgId(org.organizationId);
    setEditPullDaysVal(org.pullDays ?? 0);
    onPullDaysOpen();
  };

  const submitPullDays = () => {
    if (!selectedOrgId) return;
    updatePullDaysMutation(
      {
        orgId: selectedOrgId,
        pullDays: Number(editPullDaysVal),
      },
      {
        onSuccess: () => {
          newToast({ message: 'Pull days updated', status: 'success' });
          onPullDaysClose();
          refetch();
        },
        onError: () => {
          newToast({ message: 'Failed to update pull days', status: 'error' });
        },
      }
    );
  };

  const openPlanModal = (org: Organization) => {
    setSelectedOrgId(org.organizationId);
    setEditPlanVal(org.subscriptionPlan || '');
    setEditEndDateVal(
      org.subscriptionEndDate ? org.subscriptionEndDate.split('T')[0] : ''
    );
    onPlanOpen();
  };

  const submitPlan = () => {
    if (!selectedOrgId) return;
    if (!editPlanVal && !editEndDateVal) {
      newToast({ message: 'Provide plan or end date', status: 'warning' });
      return;
    }
    updateSubscriptionPlanMutation(
      {
        orgId: selectedOrgId,
        plan: editPlanVal || undefined,
        endDate: editEndDateVal || undefined,
      },
      {
        onSuccess: () => {
          newToast({ message: 'Subscription updated', status: 'success' });
          onPlanClose();
          refetch();
        },
        onError: () => {
          newToast({
            message: 'Failed to update subscription',
            status: 'error',
          });
        },
      }
    );
  };

  const openModulesModal = (org: Organization) => {
    setSelectedOrgId(org.organizationId);
    setEditModules(org.enabledModules ? { ...org.enabledModules } : {});
    onModulesOpen();
  };

  const handleIntegrationToggleClick = (org: Organization) => {
    setSelectedOrgId(org.organizationId);
    const hasActiveIntegrations =
      org.activeIntegrations && org.activeIntegrations.length > 0;
    setIntegrationToggleAction(
      hasActiveIntegrations ? 'deactivate' : 'reactivate'
    );
    onIntegrationToggleOpen();
  };

  const handleConfirmIntegrationToggle = () => {
    if (!selectedOrgId || !integrationToggleAction) return;

    const isDeactivating = integrationToggleAction === 'deactivate';
    const mutation = isDeactivating
      ? deactivateAllIntegrationsMutation
      : reactivateIntegrationsMutation;

    mutation(
      { orgId: selectedOrgId },
      {
        onSuccess: () => {
          newToast({
            message: isDeactivating
              ? 'All integrations deactivated successfully'
              : 'Integrations reactivated successfully',
            status: 'success',
          });
          onIntegrationToggleClose();
          setIntegrationToggleAction(null);
          refetch();
        },
        onError: () => {
          newToast({
            message: isDeactivating
              ? 'Failed to deactivate integrations'
              : 'Failed to reactivate integrations',
            status: 'error',
          });
        },
      }
    );
  };

  const countEnabledModules = (
    modules: EnabledModules | null | undefined
  ): number => {
    if (!modules) return 0;
    return Object.values(modules).filter((value) => value === true).length;
  };

  const formatDateUTC = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    const [y, m, dd] = d.toISOString().slice(0, 10).split('-');
    return `${dd}/${m}/${y}`;
  };

  const formatNumber = (value: number): string =>
    Number.isFinite(value) ? value.toLocaleString() : '0';

  const formatTokens = (tokens: number): string => {
    if (!Number.isFinite(tokens)) return '0';
    if (tokens >= 1_000_000) {
      return `${(tokens / 1_000_000).toFixed(2)}M`;
    }
    if (tokens >= 1_000) {
      return `${(tokens / 1_000).toFixed(2)}K`;
    }
    return tokens.toLocaleString();
  };

  const formatUsd = (value: number): string =>
    Number.isFinite(value) ? value.toFixed(2) : '0.00';

  const columnHelper = createColumnHelper<Organization>();

  const columns: ColumnDef<Organization, any>[] = useMemo(
    () => [
      columnHelper.accessor('organizationName', {
        id: 'organizationDetails',
        header: 'Organization Details',
        enableSorting: false,
        size: 280,
        cell: (info) => {
          const org = info.row.original;
          return (
            <Flex
              flexDirection="column"
              gap={2}
              minH="60px"
              position="relative"
            >
              <Flex justify="space-between" align="start">
                <Text fontSize="sm" fontWeight="500" color="text.primary">
                  {org.organizationName}
                </Text>
                <HStack spacing={1}>
                  <Tooltip label="Show Account Details">
                    <IconButton
                      aria-label="Show account details"
                      icon={<InfoIcon width={16} height={16} />}
                      size="xs"
                      variant="ghost"
                      onClick={() =>
                        handleShowAccountDetails(org.organizationId)
                      }
                      colorScheme="blue"
                    />
                  </Tooltip>
                </HStack>
              </Flex>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  ID: {org.organizationId}
                </Text>
                <Text fontSize="xs" color="text.secondary">
                  Plan: {org.subscriptionPlan}
                  <Tooltip label="Edit plan / end date">
                    <IconButton
                      aria-label="Edit plan"
                      icon={<EditPencilIcon width={10} height={10} />}
                      size="xs"
                      variant="ghost"
                      onClick={() => openPlanModal(org)}
                    />
                  </Tooltip>
                </Text>
                <Text fontSize="xs" color="text.secondary">
                  Start: {formatDateUTC(org.subscriptionStartDate)}
                </Text>
                <Text fontSize="xs" color="text.secondary">
                  End: {formatDateUTC(org.subscriptionEndDate)}
                </Text>
                <Text fontSize="xs" color="text.secondary">
                  Pull days: {org.pullDays}
                  <IconButton
                    aria-label="Edit pull days"
                    icon={<EditPencilIcon width={10} height={10} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => openPullDaysModal(org)}
                  />
                </Text>
              </VStack>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('activeIntegrations', {
        id: 'integrations',
        header: 'Integrations',
        enableSorting: false,
        size: 300,
        cell: (info) => {
          const integrations = info.getValue();
          const org = info.row.original;
          const hasActiveIntegrations = integrations && integrations.length > 0;
          const planLower = org.subscriptionPlan?.toLowerCase() || '';
          const isFreePlan =
            planLower === 'free_m' || planLower.startsWith('free_');
          const toggleButtonText = hasActiveIntegrations
            ? 'Deactivate All'
            : 'Reactivate';

          return (
            <Flex flexDirection="column" gap={2} minH="60px">
              {hasActiveIntegrations ? (
                <Flex flexWrap="wrap" gap={2}>
                  {integrations.map(
                    (integration: ActiveIntegration, index: number) => {
                      const displayName =
                        integration.integration === 'cloud'
                          ? integration.integrationType === 'git'
                            ? 'GitHub'
                            : 'Jira'
                          : integration.integration.charAt(0).toUpperCase() +
                            integration.integration.slice(1);
                      return (
                        <Button
                          key={index}
                          size="sm"
                          color={'text.primary'}
                          variant="custom-outline"
                          onClick={() =>
                            handleIntegrationClick(
                              integration,
                              org.organizationId
                            )
                          }
                        >
                          {displayName}
                        </Button>
                      );
                    }
                  )}
                  {isFreePlan && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => handleIntegrationToggleClick(org)}
                      fontSize="xs"
                      color="red.500"
                      _hover={{
                        textDecoration: 'underline',
                      }}
                      p={0}
                      h="auto"
                      fontWeight="normal"
                    >
                      {toggleButtonText}
                    </Button>
                  )}
                </Flex>
              ) : (
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text color="text.secondary3" fontStyle="italic">
                    No integrations
                  </Text>
                  {isFreePlan && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => handleIntegrationToggleClick(org)}
                      fontSize="xs"
                      color="green.500"
                      _hover={{
                        textDecoration: 'underline',
                      }}
                      p={0}
                      h="auto"
                      fontWeight="normal"
                    >
                      {toggleButtonText}
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>
          );
        },
      }),
      columnHelper.accessor('enabledModules', {
        id: 'enabledModules',
        header: 'Enabled Modules',
        enableSorting: false,
        size: 150,
        cell: (info) => {
          const modules = info.getValue();
          const org = info.row.original;
          const count = countEnabledModules(modules);
          return (
            <Flex flexDirection="column" justify="center" minH="60px" gap={1}>
              <HStack spacing={2}>
                <Text fontWeight="500" fontSize="sm" color="text.primary">
                  {count}
                </Text>
                <Tooltip label="Edit modules">
                  <IconButton
                    aria-label="Edit modules"
                    icon={<EditPencilIcon width={12} height={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => openModulesModal(org)}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          );
        },
      }),
      columnHelper.display({
        id: 'aiChats',
        header: 'AI Chats',
        enableSorting: false,
        size: 260,
        cell: (info) => {
          const org = info.row.original;
          const usage = tokenUsageByOrg[org.organizationId];

          if (isLoadingTokenUsage && !usage) {
            return (
              <Flex
                flexDirection="column"
                justify="center"
                minH="60px"
                align="flex-start"
              >
                <Spinner size="sm" />
              </Flex>
            );
          }

          if (!usage) {
            return (
              <Text color="text.secondary3" fontStyle="italic">
                No AI chat data
              </Text>
            );
          }

          return (
            <VStack align="start" spacing={1} minH="60px" justify="center">
              <HStack spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  Messages:
                </Text>
                <Text fontSize="xs" color="text.primary" fontWeight="500">
                  {formatNumber(usage.message_count)}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  Input tokens:
                </Text>
                <Text fontSize="xs" color="text.primary" fontWeight="500">
                  {formatTokens(usage.input_tokens)}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  Output tokens:
                </Text>
                <Text fontSize="xs" color="text.primary" fontWeight="500">
                  {formatTokens(usage.output_tokens)}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  Total tokens:
                </Text>
                <Text fontSize="xs" color="text.primary" fontWeight="500">
                  {formatTokens(usage.total_tokens)}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  Total cost:
                </Text>
                <Text fontSize="xs" color="text.primary" fontWeight="500">
                  ${formatUsd(usage.total_cost_usd)}
                </Text>
              </HStack>
              <HStack spacing={1}>
                <Text fontSize="xs" color="text.secondary">
                  Avg / message:
                </Text>
                <Text fontSize="xs" color="text.primary" fontWeight="500">
                  ${formatUsd(usage.avg_cost_per_message_usd)}
                </Text>
              </HStack>
            </VStack>
          );
        },
      }),
    ],
    [tokenUsageByOrg, isLoadingTokenUsage]
  );

  const FullWidthWrapper = styled.div`
    width: 100%;
    & > div {
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 100% !important;
    }
  `;

  return (
    <Flex w="full" direction="column" gap={4}>
      <PageHeader
        headingText="Active Organizations"
        subHeadingText="View and manage active organizations, their integrations, and subscription details"
        component={
          <Box minW={{ base: '100%', md: '320px' }}>
            <SearchInputBar
              searchValue={search}
              setSearchValue={(val: string) => setSearch(val)}
              placeholder="Search by name or ID"
              size="sm"
            />
          </Box>
        }
      />

      <FullWidthWrapper>
        <NewDataGrid
          data={organizations}
          columns={columns}
          showLoader={isLoading}
          sticky="none"
          useMaxH
          maxH={paginationData.totalPages > 1 ? '65vh' : '70vh'}
          hideHeaderTooltip
          showGridLines
        />
      </FullWidthWrapper>

      {paginationData.totalPages > 0 && (
        <Box>
          <Paginate
            currentPage={paginationData.currentPage + 1}
            totalPageCount={paginationData.totalPages}
            pageSize={pageSize}
            onPageChange={(page) => handlePageClick(page as number)}
          />
        </Box>
      )}

      {isIntegrationModalOpen && (
        <IntegrationDetailsModal
          isOpen={isIntegrationModalOpen}
          onClose={onIntegrationModalClose}
          integration={selectedIntegration}
          orgId={selectedOrgId}
          isLoading={isLoadingIntegration}
        />
      )}
      <AlertDialog
        isOpen={isWarningOpen}
        leastDestructiveRef={cancelRef}
        onClose={onWarningClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Warning
            </AlertDialogHeader>

            <AlertDialogBody>
              Retrieving Sensitive info, your detail will be audited
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onWarningClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleAccept} ml={3}>
                Accept
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={isDetailsOpen} onClose={onDetailsClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Account Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isLoadingDetails ? (
              <Center p={4}>
                <Spinner />
              </Center>
            ) : (
              loginDetails && (
                <VStack align="stretch" spacing={4}>
                  {Object.entries(loginDetails).map(([email, password]) => (
                    <VStack key={email} align="stretch" spacing={2}>
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Email:</Text>
                        <Text>{email}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Password:</Text>
                        <Text>{password}</Text>
                      </HStack>
                    </VStack>
                  ))}
                </VStack>
              )
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPullDaysOpen} onClose={onPullDaysClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Pull Days</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Pull Days
                </Text>
                <NumberInput
                  size="sm"
                  min={0}
                  value={editPullDaysVal}
                  onChange={(v) => setEditPullDaysVal(Number(v))}
                >
                  <NumberInputField />
                </NumberInput>
              </Box>
              <HStack justify="flex-end">
                <Button size="sm" variant="ghost" onClick={onPullDaysClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="filled"
                  isLoading={isUpdatingPullDays}
                  onClick={submitPullDays}
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPlanOpen} onClose={onPlanClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Plan / End Date</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Plan
                </Text>
                <Select
                  size="sm"
                  value={editPlanVal}
                  onChange={(e) => setEditPlanVal(e.target.value)}
                >
                  <option value="">Select plan</option>
                  <option value="free_m">free_m</option>
                  <option value="pro_m">pro_m</option>
                  <option value="trial_m">trial_m</option>
                  <option value="business_y">business_y</option>
                  <option value="pro_y">pro_y</option>
                  <option value="business_m">business_m</option>
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Plan End Date
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={editEndDateVal}
                  onChange={(e) => setEditEndDateVal(e.target.value)}
                />
              </Box>

              <HStack justify="flex-end">
                <Button size="sm" variant="ghost" onClick={onPlanClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="filled"
                  isLoading={isUpdatingSubscription}
                  onClick={submitPlan}
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {isModulesOpen && (
        <EnabledModulesModal
          isOpen={isModulesOpen}
          onClose={onModulesClose}
          initialModules={editModules}
          onSave={async (modulesToSend) => {
            if (!selectedOrgId) return;
            updateEnabledModulesMutation(
              {
                orgId: selectedOrgId,
                modules: modulesToSend,
              },
              {
                onSuccess: () => {
                  newToast({ message: 'Modules updated', status: 'success' });
                  onModulesClose();
                  refetch();
                },
                onError: () => {
                  newToast({
                    message: 'Failed to update modules',
                    status: 'error',
                  });
                },
              }
            );
          }}
          isSubmitting={isUpdatingModules}
        />
      )}

      {isIntegrationToggleOpen && (
        <IntegrationToggleConfirmDialog
          isOpen={isIntegrationToggleOpen}
          action={integrationToggleAction}
          isLoading={isDeactivatingIntegrations || isReactivatingIntegrations}
          onCancel={onIntegrationToggleClose}
          onConfirm={handleConfirmIntegrationToggle}
        />
      )}
    </Flex>
  );
}
